import Ajv from 'ajv';
import addFormats from 'ajv-formats';

/**
 * Service for handling credential schema operations
 */
class CredentialSchemaService {
  private ajv: Ajv;

  constructor() {
    // Allow unknown keywords like 'order' which we use for UI ordering
    this.ajv = new Ajv({
      allErrors: true,
      verbose: true,
      strict: false  // Allow unknown keywords
    });
    addFormats(this.ajv);
  }

  /**
   * Validate credentials against JSON schema
   */
  validateCredentials(schema: any, credentials: any): { valid: boolean; errors?: string[] } {
    if (!schema) {
      return { valid: true };
    }

    // Create a copy of schema without the 'order' field for validation
    const validationSchema = { ...schema };
    delete validationSchema.order;

    const validate = this.ajv.compile(validationSchema);
    const valid = validate(credentials);

    if (!valid && validate.errors) {
      // Transform AJV errors into user-friendly messages
      const errors = validate.errors.map(error => {
        const field = error.instancePath ? error.instancePath.substring(1) : error.params?.missingProperty || 'field';
        const fieldSchema = schema.properties?.[field];
        const fieldLabel = fieldSchema?.description || field;

        switch (error.keyword) {
          case 'required':
            return `${schema.properties?.[error.params?.missingProperty]?.description || error.params?.missingProperty} is required`;
          case 'type':
            return `${fieldLabel} must be a ${error.params?.type}`;
          case 'format':
            return `${fieldLabel} must be a valid ${error.params?.format}`;
          case 'minimum':
          case 'exclusiveMinimum':
            return `${fieldLabel} must be at least ${error.params?.limit}`;
          case 'maximum':
          case 'exclusiveMaximum':
            return `${fieldLabel} must be at most ${error.params?.limit}`;
          case 'minLength':
            return `${fieldLabel} must be at least ${error.params?.limit} characters`;
          case 'maxLength':
            return `${fieldLabel} must be at most ${error.params?.limit} characters`;
          case 'pattern':
            return `${fieldLabel} format is invalid`;
          case 'enum':
            return `${fieldLabel} must be one of: ${error.params?.allowedValues?.join(', ')}`;
          default:
            return error.message || `${fieldLabel} is invalid`;
        }
      });

      // Remove duplicates
      const uniqueErrors = [...new Set(errors)];

      return {
        valid: false,
        errors: uniqueErrors
      };
    }

    return { valid: true };
  }

  /**
   * Render a field based on its schema
   */
  renderField(name: string, config: any, isRequired: boolean, value: any, onChange: (name: string, value: any) => void) {
    const label = config.description || name;
    const placeholder = config.example || '';
    const defaultValue = config.default;

    const commonProps = {
      name,
      id: name,
      autocomplete: 'off',
      className: 'input input-bordered w-full',
      required: isRequired
    };

    switch (config.type) {
      case 'number':
      case 'integer':
        return (
          <div className="form-control" key={name}>
            <label className="label" htmlFor={name}>
              <span className="label-text">{label}</span>
              {isRequired && <span className="label-text-alt text-error">*</span>}
            </label>
            <input
              {...commonProps}
              type="number"
              placeholder={placeholder || defaultValue?.toString()}
              value={value !== undefined ? value : (defaultValue ?? '')}
              onInput={(e) => {
                const val = (e.target as HTMLInputElement).value;
                onChange(name, val === '' ? undefined : Number(val));
              }}
              min={config.minimum}
              max={config.maximum}
            />
            {config.description && (
              <label className="label">
                <span className="label-text-alt">{config.description}</span>
              </label>
            )}
          </div>
        );

      case 'boolean':
        return (
          <div className="form-control" key={name}>
            <label className="label cursor-pointer" htmlFor={name}>
              <span className="label-text">{label}</span>
              <input
                {...commonProps}
                type="checkbox"
                className="checkbox"
                checked={value ?? defaultValue ?? false}
                onChange={(e) => onChange(name, (e.target as HTMLInputElement).checked)}
                required={false}
              />
            </label>
            {config.description && (
              <label className="label">
                <span className="label-text-alt">{config.description}</span>
              </label>
            )}
          </div>
        );

      case 'string':
      default:
        const isPassword = name.toLowerCase().includes('password') ||
                          name.toLowerCase().includes('secret') ||
                          name.toLowerCase().includes('key') ||
                          config.format === 'password';

        return (
          <div className="form-control" key={name}>
            <label className="label" htmlFor={name}>
              <span className="label-text">{label}</span>
              {isRequired && <span className="label-text-alt text-error">*</span>}
            </label>
            <input
              {...commonProps}
              type={isPassword ? 'password' : 'text'}
              placeholder={placeholder || defaultValue}
              value={value ?? defaultValue ?? ''}
              onInput={(e) => onChange(name, (e.target as HTMLInputElement).value)}
              minLength={config.minLength}
              maxLength={config.maxLength}
            />
            {config.description && (
              <label className="label">
                <span className="label-text-alt">{config.description}</span>
              </label>
            )}
          </div>
        );
    }
  }

  /**
   * Render all fields from a schema
   */
  renderSchemaFields(schema: any, credentials: Record<string, any>, onChange: (name: string, value: any) => void) {
    if (!schema || !schema.properties) {
      return (
        <div className="text-center text-gray-500">
          No configuration required
        </div>
      );
    }

    const required = schema.required || [];
    const fields = [];

    // Determine field order
    let fieldOrder: string[];
    if (schema.order && Array.isArray(schema.order)) {
      // Use explicit order if provided
      fieldOrder = schema.order;
      // Add any fields not in the order array at the end
      const orderedSet = new Set(fieldOrder);
      for (const name of Object.keys(schema.properties)) {
        if (!orderedSet.has(name)) {
          fieldOrder.push(name);
        }
      }
    } else {
      // Use default ordering: required fields first, then optional
      const requiredFields = [];
      const optionalFields = [];

      for (const name of Object.keys(schema.properties)) {
        if (required.includes(name)) {
          requiredFields.push(name);
        } else {
          optionalFields.push(name);
        }
      }

      fieldOrder = [...requiredFields, ...optionalFields];
    }

    // Render fields in the determined order
    for (const name of fieldOrder) {
      const config = schema.properties[name];
      if (config) {
        const isRequired = required.includes(name);
        fields.push(this.renderField(name, config as any, isRequired, credentials[name], onChange));
      }
    }

    return <div className="space-y-4">{fields}</div>;
  }
}

export const credentialSchemaService = new CredentialSchemaService();
